// components/purchase/ReceptionDetailsView.tsx
import { useState } from 'react';
import toast from 'react-hot-toast';

interface LigneReception {
  id: number;
  produit: string;
  quantite_commandee: number;
  quantite_recue: number;
  quantite_rejetee: number;
  motif_rejet?: string;
  action_non_conformite?: string;
}

interface Reception {
  id: number;
  numero: string;
  bc_numero: string;
  fournisseur: string;
  date_reception: string;
  utilisateur_nom: string;
  total_commandee: number;
  total_recue_cumule: number;
  quantite_dans_ce_br: number;
  total_restante: number;
  has_non_conformite: boolean;
  observations?: string;
  emplacement_nom?: string;
  lignes: LigneReception[];
  bon_retour?: any; // si déjà créé
}

interface ReceptionDetailsViewProps {
  reception: Reception;
  onClose: () => void;
  onCreateRetour?: (reception: Reception) => void;
}

const formatAmount = (amount: number, devise = 'GNF') => {
  if (!amount) return '-';
  return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + devise;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR');
};

const printBR = (reception: Reception) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error("Impossible d'ouvrir l'impression");
    return;
  }

  const lignes = reception.lignes || [];

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>BR ${reception.numero}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #10b981; padding-bottom: 15px; }
        .title { font-size: 28px; font-weight: bold; color: #059669; }
        .info { display: flex; justify-content: space-between; margin: 30px 0; background: #f0fdf4; padding: 15px; border-radius: 8px; }
        table { width: 100%; border-collapse: collapse; margin: 30px 0; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
        th { background: #f0fdf4; font-weight: bold; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .non-conf { background: #fee2e2; }
        .footer { margin-top: 60px; text-align: right; }
        .signature { margin-top: 80px; display: flex; justify-content: space-between; }
        .signature div { width: 280px; text-align: center; border-top: 1px solid #000; padding-top: 10px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="title">BON DE RÉCEPTION FOURNISSEUR</h1>
        <h2>N° ${reception.numero}</h2>
      </div>

      <div class="info">
        <div>
          <strong>BC :</strong> ${reception.bc_numero}<br>
          <strong>Fournisseur :</strong> ${reception.fournisseur}<br>
          <strong>Date réception :</strong> ${formatDate(reception.date_reception)}<br>
          <strong>Réceptionné par :</strong> ${reception.utilisateur_nom}
        </div>
        <div class="text-right">
          ${reception.emplacement_nom ? `<strong>Emplacement :</strong> ${reception.emplacement_nom}<br>` : ''}
          <strong>Observations :</strong><br>
          ${reception.observations || 'Aucune'}
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Produit</th>
            <th class="text-center">Qté commandée</th>
            <th class="text-center">Qté reçue</th>
            <th class="text-center">Conforme</th>
            <th class="text-center">Rejetée</th>
            <th>Motif rejet</th>
          </tr>
        </thead>
        <tbody>
          ${lignes.map(l => `
            <tr ${l.quantite_rejetee > 0 ? 'class="non-conf"' : ''}>
              <td><strong>${l.produit}</strong></td>
              <td class="text-center">${l.quantite_commandee}</td>
              <td class="text-center">${l.quantite_recue + l.quantite_rejetee}</td>
              <td class="text-center text-green-600 font-bold">${l.quantite_recue}</td>
              <td class="text-center text-red-600 font-bold">${l.quantite_rejetee}</td>
              <td>${l.quantite_rejetee > 0 ? (l.motif_rejet || 'Non précisé') : '—'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="footer">
        <p><strong>Total commandé :</strong> ${reception.total_commandee} unités</p>
        <p><strong>Total reçu cumulé :</strong> ${reception.total_recue_cumule} unités</p>
        <p><strong>Reçu dans ce BR :</strong> ${reception.quantite_dans_ce_br} unités</p>
        ${reception.total_restante > 0 ? `<p><strong>En attente :</strong> ${reception.total_restante} unités</p>` : '<p style="color:green;font-weight:bold">Réception complète</p>'}
      </div>

      <div class="signature">
        <div>Cachet & Signature Fournisseur</div>
        <div>Cachet & Signature Magasinier</div>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 500);
  printWindow.onafterprint = () => printWindow.close();
};

export default function ReceptionDetailsView({ reception, onClose, onCreateRetour }: ReceptionDetailsViewProps) {
  const [loadingPrint, setLoadingPrint] = useState(false);

  const handlePrintBR = () => {
    setLoadingPrint(true);
    printBR(reception);
    setTimeout(() => setLoadingPrint(false), 1000);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* En-tête */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg hover:bg-gray-200"
              title="Retour"
            >
              <i className="ri-arrow-left-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Bon de Réception {reception.numero}
              </h2>
              <p className="text-sm text-gray-600 mt-1">Détails de la réception fournisseur</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Bouton Imprimer BR */}
            <button
              onClick={handlePrintBR}
              disabled={loadingPrint}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2"
            >
              {loadingPrint ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-printer-line"></i>
              )}
              Imprimer BR
            </button>

            {/* Bouton Créer Bon de Retour */}
            {reception.has_non_conformite && !reception.bon_retour && (
              <button
                onClick={() => onCreateRetour?.(reception)}
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2"
              >
                <i className="ri-arrow-go-back-line"></i>
                Créer Retour
              </button>
            )}

            {/* Bouton Imprimer Bon de Retour s’il existe */}
            {reception.bon_retour && (
              <button
                onClick={() => printBonRetour(reception.bon_retour)}
                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2"
              >
                <i className="ri-file-text-line"></i>
                Imprimer Retour
              </button>
            )}
          </div>
        </div>

        {/* Statut de réception */}
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-full ${
            reception.total_restante === 0 
              ? 'bg-green-100 text-green-800' 
              : reception.total_recue_cumule > reception.total_commandee 
                ? 'bg-purple-100 text-purple-800'
                : 'bg-orange-100 text-orange-800'
          }`}>
            <i className={
              reception.total_restante === 0 
                ? 'ri-checkbox-circle-fill' 
                : 'ri-time-line'
            }></i>
            {reception.total_restante === 0 ? 'Réception complète' : 'Réception partielle'}
          </span>

          {reception.has_non_conformite && (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-800 text-sm font-semibold rounded-full">
              <i className="ri-error-warning-line"></i>
              Non-conformités détectées
            </span>
          )}
        </div>
      </div>

      {/* Infos générales */}
      <div className="p-6 border-b">
        <h3 className="text-lg font-semibold mb-4">Informations</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="text-sm text-gray-500">BC d'origine</label>
            <p className="font-semibold">{reception.bc_numero}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Fournisseur</label>
            <p className="font-semibold">{reception.fournisseur}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Date réception</label>
            <p className="font-semibold">{formatDate(reception.date_reception)}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Réceptionné par</label>
            <p className="font-semibold">{reception.utilisateur_nom}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500">Emplacement</label>
            <p className="font-semibold">{reception.emplacement_nom || '—'}</p>
          </div>
        </div>
      </div>

      {/* Progression globale */}
      <div className="p-6 bg-gray-50 border-b">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
          <div>
            <p className="text-sm text-gray-500">Commandée</p>
            <p className="text-2xl font-bold">{reception.total_commandee}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Reçu cumulé</p>
            <p className="text-2xl font-bold text-green-600">{reception.total_recue_cumule}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Dans ce BR</p>
            <p className="text-2xl font-bold text-blue-600">+{reception.quantite_dans_ce_br}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">En attente</p>
            <p className={`text-2xl font-bold ${reception.total_restante > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {reception.total_restante}
            </p>
          </div>
        </div>
      </div>

      {/* Tableau des lignes */}
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4">Détail des lignes</h3>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs uppercase text-gray-500">Produit</th>
                <th className="px-6 py-3 text-center text-xs uppercase text-gray-500">Qté commandée</th>
                <th className="px-6 py-3 text-center text-xs uppercase text-gray-500">Reçue totale</th>
                <th className="px-6 py-3 text-center text-xs uppercase text-gray-500">Conforme</th>
                <th className="px-6 py-3 text-center text-xs uppercase text-red-600">Rejetée</th>
                <th className="px-6 py-3 text-left text-xs uppercase text-gray-500">Motif</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {reception.lignes.map(l => (
                <tr key={l.id} className={l.quantite_rejetee > 0 ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4 font-medium">{l.produit}</td>
                  <td className="px-6 py-4 text-center">{l.quantite_commandee}</td>
                  <td className="px-6 py-4 text-center font-semibold">
                    {l.quantite_recue + l.quantite_rejetee}
                  </td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">{l.quantite_recue}</td>
                  <td className="px-6 py-4 text-center text-red-600 font-bold">{l.quantite_rejetee}</td>
                  <td className="px-6 py-4 text-sm">
                    {l.quantite_rejetee > 0 ? l.motif_rejet || 'Non précisé' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Observations */}
      {reception.observations && (
        <div className="p-6 border-t bg-gray-50">
          <label className="text-sm text-gray-500">Observations</label>
          <p className="mt-1 text-gray-800">{reception.observations}</p>
        </div>
      )}
    </div>
  );
}